import React, { ComponentPropsWithoutRef } from 'react';
import {
  Box,
  BoxProps,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import useCopyToClipboard from 'react-use/lib/useCopyToClipboard';

import ArrowRightIcon from '@watershed/icons/components/ArrowRight';
import CopyIcon from '@watershed/icons/components/Copy';
import PopOutIcon from '@watershed/icons/components/PopOut';
import type { IconElement } from '@watershed/icons/Icon';
// eslint-disable-next-line import/no-extraneous-dependencies
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import Tooltip from './Tooltip';
// TODO: i18n (please resolve or remove this TODO line if legit)
// eslint-disable-next-line no-restricted-imports
import { humanize } from '@watershed/shared-universal/utils/helpers';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import mixinSx from '@watershed/style/mixinSx';

import usePaletteUtils from '../hooks/usePaletteUtils';
import { WatershedLinkProps } from '../types/LinkProps';
import {
  navigateToPathOnClick,
  navigateToPathOnKeypress,
} from '../utils/NavigationUtils';
import BoxLink from './BoxLink';

const tableRootSx: SxProps<Theme> = { tableLayout: 'fixed' };

const tableHeadSx: SxProps<Theme> = {
  '& th': {
    padding: '6px 8px',
    borderTop: 'none !important',
    borderBottom: 'none !important',
  },
};

const tableRowSx: SxProps<Theme> = {
  backgroundColor: (theme) => theme.palette.grey10,
  transition: (theme) => theme.transitions.create('background-color'),
  '&:first-of-type td:first-of-type': {
    overflow: 'hidden',
    borderTopLeftRadius: '4px',
  },
  '&:first-of-type td:last-child': {
    overflow: 'hidden',
    borderTopRightRadius: '4px',
  },
  '&:last-child td:first-of-type': {
    overflow: 'hidden',
    borderBottomLeftRadius: '4px',
  },
  '&:last-child td:last-child': {
    overflow: 'hidden',
    borderBottomRightRadius: '4px',
  },
  '&.Mui-selected , &.Mui-selected:hover': {
    backgroundColor: (theme) => theme.palette.cobalt20,
  },
};

const tableRowClickableSx: SxProps<Theme> = {
  '& td, & a, & .MuiChip-root': {
    color: (theme) => theme.palette.text.primary,
    cursor: 'pointer',
    transition: (theme) => theme.transitions.create('color'),
  },
  '& a': {
    textDecoration: 'none',
  },
  '&:hover, &:focus': {
    backgroundColor: (theme) => theme.palette.grey20,
    '& td, & a, & .MuiChip-root, & svg': {
      color: (theme) => theme.palette.primary.main,
    },
  },
  '& td:focus': {
    backgroundColor: (theme) => theme.palette.grey20,
    color: (theme) => theme.palette.primary.main,
    outline: 'none',
  },
};

const tableCellSx: SxProps<Theme> = {
  padding: '6px 8px',
  verticalAlign: 'top',
  borderBottom: (theme) => `1px solid ${theme.palette.background.paper}`,
  WebkitTapHighlightColor: 'transparent',
  '& + &': {
    borderLeft: (theme) => `1px solid ${theme.palette.background.paper}`,
  },
  '& .MuiInputBase-root': {
    lineHeight: 'inherit',
    width: '100%',
  },
  '& .MuiInputBase-input.MuiSelect-select': {
    background: 'transparent',
    boxShadow: 'none !important',
    borderRadius: 0,
    margin: '-6px -8px',
    padding: '6px 8px !important',
    cursor: 'pointer',
    transition: (theme) =>
      theme.transitions.create(['color', 'background-color']),
    '&:not(.Mui-disabled):hover, &:not(.Mui-disabled):focus': {
      backgroundColor: (theme) => theme.palette.grey20,
      color: (theme) => theme.palette.primary.main,
      outline: 'none',
    },
  },
  '& .MuiSelect-nativeInput': {
    lineHeight: 'inherit',
  },
  '& .MuiSelect-icon': {
    right: 0,
  },
  '&:last-child .MuiIconButton-root': {
    marginTop: (theme) => theme.spacing(-0.5),
    marginBottom: (theme) => theme.spacing(-0.5),
  },
};

const tableCellLinkSx: SxProps<Theme> = {
  overflowWrap: 'break-word',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textAlign: 'left',
  pointerEvents: 'all',
  margin: '-6px -8px',
  padding: '6px 8px !important',
  width: 'calc(100% + 16px)',
  cursor: 'pointer',
  transition: (theme) =>
    theme.transitions.create(['color', 'background-color']),
  '& svg': {
    transition: (theme) => theme.transitions.create('color'),
  },
  '&:hover, &:focus': {
    backgroundColor: (theme) => theme.palette.grey20,
    color: (theme) => theme.palette.primary.main,
    outline: 'none',
    '& svg': {
      color: (theme) => theme.palette.primary.main,
    },
  },
};

const tableCellButtonSx: SxProps<Theme> = {
  appearance: 'none',
  border: 0,
  background: 'none',
  font: 'inherit',
  color: 'inherit',
  letterSpacing: 'inherit',
};

const tableCellTextOverflowEllipsisSx: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

const tableCellCompactSx: SxProps<Theme> = {
  borderBottomWidth: '4px',
};

export interface IndexTableBaseProps {
  label?: LocalizedString;
  labeledBy?: string;
  columns: Array<
    | string
    | {
        id: string;
        title?: LocalizedString; // overriden if tooltip is present
        tooltip?: JSX.Element;
        disableClick?: boolean;
        width?: number | string;
        textOverflowEllipsis?: boolean;
        columnCellStyles?: React.CSSProperties;
      }
    | undefined
  >;
  columnLinkIndex?: number;
  rows: Array<{
    path?: string | null;
    openInNewTab?: boolean;
    id: string;
    style?: React.CSSProperties;
    selected?: boolean;
    onClick?: (e: React.SyntheticEvent) => void;
    fields: Record<string, string | React.ReactNode>;
  }>;
  hideHead?: boolean;
  stickyHeader?: boolean;
  tableStyle?: React.CSSProperties;
  tableContainerStyle?: React.CSSProperties;
  children?: React.ReactNode;
  compact?: boolean;
  alwaysShowHorizontalScrollbar?: boolean;
}

export type IndexTableProps = BoxProps & IndexTableBaseProps;

/** @deprecated Please use DataGrid or ObjectList instead */
export default function IndexTable({
  label,
  labeledBy,
  columns,
  columnLinkIndex = 0,
  rows,
  hideHead = false,
  stickyHeader,
  children,
  tableStyle,
  tableContainerStyle,
  compact,
  alwaysShowHorizontalScrollbar,
  ...props
}: IndexTableProps) {
  const router = useRouter();
  const cols = columns.filter(isNotNullish).map((col) => {
    if (typeof col === 'object') return col;
    return {
      id: col,
    };
  });
  const paletteUtils = usePaletteUtils();
  const autoWidthColumnCount =
    cols.length - cols.map((col) => col.width).filter(isNotNullish).length;
  return (
    <Box paddingLeft={1} paddingRight={1} paddingBottom={1} {...props}>
      {children}
      <TableContainer
        sx={mixinSx(
          tableContainerStyle,
          alwaysShowHorizontalScrollbar &&
            paletteUtils.alwaysShowWebkitScrollbar
        )}
      >
        <Table
          aria-label={label}
          aria-labelledby={labeledBy}
          size="medium"
          sx={tableRootSx}
          stickyHeader={stickyHeader}
          style={tableStyle}
        >
          <TableHead
            sx={mixinSx(
              tableHeadSx,
              hideHead || compact ? visuallyHidden : undefined
            )}
          >
            <TableRow>
              {cols.filter(Boolean).map((col) => (
                <TableCell
                  key={col.id}
                  style={{
                    width:
                      (typeof col.width === 'number'
                        ? col.width + 16
                        : col.width) ?? `${100 / autoWidthColumnCount}%`,
                  }}
                >
                  {col.tooltip ??
                    col.title ?? // if both column tooltip or title are not provided, this is empty
                    humanize(col.id)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                data-testid={`TableRow-${i}`}
                key={row.id}
                sx={mixinSx(
                  tableRowSx,
                  !!(row.path || row.onClick) && tableRowClickableSx
                )}
                style={row?.style}
                selected={row?.selected}
              >
                {cols.filter(Boolean).map((col, i) => {
                  const { disableClick = false } = col;
                  const onClick = (
                    event: React.MouseEvent<HTMLTableHeaderCellElement>
                  ) => {
                    if (disableClick) {
                      return false;
                    }
                    if (row.onClick) {
                      row.onClick(event);
                    }
                    if (row.path) {
                      navigateToPathOnClick(event, router, row.path, {
                        openInNewTab: row.openInNewTab,
                      });
                    }
                  };
                  /*
                    This rendering is a bit complex because the first cell should contain a link
                    for semantic HTML, but each cell should navigate on click anywhere on the cell
                  */
                  const linkedCellForRow = i === columnLinkIndex && row.path;
                  return (
                    <TableCell
                      key={col.id}
                      sx={mixinSx(
                        tableCellSx,
                        col.textOverflowEllipsis &&
                          tableCellTextOverflowEllipsisSx,
                        compact && tableCellCompactSx
                      )}
                      style={{
                        pointerEvents: disableClick ? 'none' : undefined,
                        ...col.columnCellStyles,
                      }}
                      tabIndex={linkedCellForRow ? 0 : undefined}
                      role={linkedCellForRow ? 'link' : undefined}
                      onKeyDown={
                        linkedCellForRow
                          ? (e) => {
                              if (e.key === 'Enter' && row.path) {
                                navigateToPathOnKeypress(e, router, row.path);
                              }
                            }
                          : undefined
                      }
                      onClick={onClick}
                      onAuxClick={onClick}
                    >
                      {row.fields[col.id]}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export function IndexTableCellLink({
  href,
  color = 'text.primary',
  children,
  icon,
  iconTooltip,
  textOverflowEllipsis,
  newTab,
  sx,
  ...props
}: Omit<BoxProps, 'ref'> & {
  href?: WatershedLinkProps['href'];
  newTab?: WatershedLinkProps['newTab'];
  children: React.ReactNode;
  icon?: IconElement;
  iconTooltip?: LocalizedString;
  textOverflowEllipsis?: boolean;
}) {
  const Icon = icon ?? (newTab ? PopOutIcon : ArrowRightIcon);
  const iconComponent = (
    <Icon
      height={20}
      width={28}
      color={color === 'primary.main' ? 'inherit' : 'secondary.main'}
      paddingLeft={1}
      flexShrink={0}
      alignSelf="center"
      data-html2canvas-ignore
    />
  );
  return (
    <BoxLink
      href={href}
      {...omit(props, ['onMouseEnter', 'onTouchStart'])}
      color={color}
      sx={mixinSx(tableCellLinkSx, sx)}
    >
      {textOverflowEllipsis ? (
        <Box component="span" sx={tableCellTextOverflowEllipsisSx}>
          {children}
        </Box>
      ) : (
        children
      )}
      {iconTooltip ? (
        <Tooltip title={iconTooltip}>
          <div style={{ lineHeight: 0 }}>{iconComponent}</div>
        </Tooltip>
      ) : (
        iconComponent
      )}
    </BoxLink>
  );
}

export function IndexTableCellButton({
  icon,
  iconTooltip,
  onClick,
  sx,
  ...props
}: {
  icon?: IconElement;
  iconTooltip?: LocalizedString;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
} & ComponentPropsWithoutRef<typeof IndexTableCellLink>) {
  return (
    <IndexTableCellLink
      component="button"
      icon={icon}
      iconTooltip={iconTooltip}
      onClick={onClick}
      sx={mixinSx(tableCellButtonSx, sx)}
      {...props}
    />
  );
}

export function IndexTableCellCopy({
  clipboardText,
  snackbarText = 'Copied!',
  ...props
}: BoxProps & {
  clipboardText?: LocalizedString;
  snackbarText?: LocalizedString;
  children: React.ReactNode;
  textOverflowEllipsis?: boolean;
}) {
  const [, copy] = useCopyToClipboard();
  const snackbar = useSnackbar();
  return (
    <IndexTableCellButton
      icon={CopyIcon}
      iconTooltip="Copy"
      onClick={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        copy(clipboardText ?? props.children?.toString() ?? '');
        snackbar.enqueueSnackbar(snackbarText);
        e.currentTarget.blur();
      }}
      {...props}
    />
  );
}
